import React, { useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import favicon from "../assets/favicon_new.png";

const BillingSection = ({
  isDarkMode,
  themeClasses,
  currentSubscription,
  billingCycle,
  setBillingCycle,
  paymentLoading,
  setPaymentLoading,
  Card,
}) => {
  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Construct the Calendly URL with theme parameters
  const calendlyUrl = isDarkMode 
    ? 'https://calendly.com/vansh-clevrscan/30min?background_color=1f2937&text_color=ffffff&primary_color=3b82f6'
    : 'https://calendly.com/vansh-clevrscan/30min';

  return (
    <div className="space-y-6">
      {/* Current Plan Details */}
      <Card
        className={`p-4 transition-all duration-300 transform hover:shadow-lg ${
          isDarkMode
            ? "bg-gray-800/50"
            : "bg-gradient-to-br from-white to-slate-50 border border-slate-200 shadow-md rounded-2xl"
        }`}
      >
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center mr-3">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2
            className={`text-xl font-bold ${
              isDarkMode ? "text-white" : "text-slate-800"
            }`}
          >
            Current Plan
          </h2>
        </div>

        {currentSubscription ? (
          <div
            className={`flex flex-col lg:flex-row lg:items-center border lg:justify-between
          ${
            isDarkMode
              ? "bg-slate-600/50 border border-slate-950 "
              : "bg-white/95 border border-slate-280"
          } rounded-xl p-4`}
          >
            <div className="mb-3 lg:mb-0">
              <div className="flex items-center mb-1">
                <h3
                  className={`text-lg font-bold ${
                    isDarkMode ? "text-white" : "text-slate-800"
                  } mr-2`}
                >
                  {currentSubscription.plan_name} Plan
                </h3>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    currentSubscription.status === "active"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {currentSubscription.status === "active"
                    ? "Active"
                    : "Inactive"}
                </span>
              </div>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-slate-600"
                } mb-0.5`}
              >
                {currentSubscription.billing_cycle === "monthly"
                  ? "Monthly"
                  : "Yearly"}{" "}
                Billing
              </p>
              <p
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-slate-500"
                }`}
              >
                Next billing date:{" "}
                {new Date(
                  Date.now() + 30 * 24 * 60 * 60 * 1000
                ).toLocaleDateString()}
              </p>
            </div>
            <div className="text-left lg:text-right">
              <p
                className={`text-2xl font-black ${
                  isDarkMode ? "text-white" : "text-slate-800"
                } mb-1`}
              >
                ${currentSubscription.amount}
                <span
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-gray-400" : "text-slate-500"
                  } ml-1`}
                >
                  {currentSubscription.billing_cycle === "monthly"
                    ? "/mo"
                    : "/yr"}
                </span>
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-5 text-center">
            <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg
                className="w-6 h-6 text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-bold ${
                isDarkMode ? "text-white" : "text-slate-800"
              } mb-2`}
            >
              Free Plan
            </h3>
            <p
              className={`${
                isDarkMode ? "text-gray-400" : "text-slate-600"
              } mb-3 text-sm`}
            >
              You're currently on the Free plan. Upgrade to unlock
              premium features.
            </p>
          </div>
        )}
      </Card>

      {/* Calendly Section */}
      <Card
        className={`p-6 transition-all duration-300 ${
          isDarkMode
            ? "bg-gray-800/50"
            : "bg-gradient-to-br from-white to-slate-50 border border-slate-200 shadow-md rounded-2xl"
        }`}
      >
        <div className="flex items-center mb-6">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center mr-3">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h2
            className={`text-xl font-bold ${
              isDarkMode ? "text-white" : "text-slate-800"
            }`}
          >
            Schedule a Meeting
          </h2>
        </div>

        <div
          className={`rounded-xl overflow-hidden ${
            isDarkMode ? "bg-gray-900/50" : "bg-white"
          }`}
        >
          {/* Using key prop to force remount when dark mode changes */}
          <div
            key={isDarkMode ? 'dark' : 'light'}
            className="calendly-inline-widget"
            data-url={calendlyUrl}
            style={{ minWidth: "320px", height: "700px" }}
          ></div>
        </div>
      </Card>
    </div>
  );
};

export default BillingSection;